/**
 * The buy panel: drag the card count, see what it costs.
 *
 * The catalogue is six packs; the ladder that used to be twenty-one products
 * now lives in this control. Everything it shows is computed locally from the
 * same generator the server prices against (`lib/packs.ts`), so the price
 * tracks the slider with no round trip and cannot disagree with the charge.
 */
import type { PackConfig } from '../lib/packs'
import { MAX_CARDS, MIN_CARDS, hitsFor, priceFor } from '../lib/packs'
import { percent, usd } from '../lib/format'
import { TIER_ACCENT } from './ui'

export function PackConfigurator({
  config,
  onChange,
  onRip,
  price,
  rtp,
  pending = false,
  disabled = false,
}: {
  config: PackConfig
  onChange: (next: PackConfig) => void
  onRip: () => void
  price: number
  rtp: number
  pending?: boolean
  disabled?: boolean
}) {
  const accent = TIER_ACCENT[config.tier]
  const hits = hitsFor(config.cards)
  const base = priceFor(config.tier, MIN_CARDS)

  // Position of the thumb as a percentage, so the filled part of the track and
  // the tick labels agree with where the handle actually sits.
  const fill = ((config.cards - MIN_CARDS) / (MAX_CARDS - MIN_CARDS)) * 100

  return (
    <div className="panel-2 p-5" style={{ ['--accent' as string]: accent }}>
      <div className="eyebrow">Build your pack</div>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <label htmlFor="card-count" className="text-sm font-medium">
            Cards in this pack
          </label>
          <span className="tnum text-2xl font-bold" style={{ color: accent }}>
            {config.cards}
          </span>
        </div>

        <input
          id="card-count"
          className="slider mt-2"
          style={{ ['--fill' as string]: `${fill}%` }}
          type="range"
          min={MIN_CARDS}
          max={MAX_CARDS}
          step={1}
          value={config.cards}
          onChange={(event) => onChange({ ...config, cards: Number(event.target.value) })}
          aria-valuetext={`${config.cards} cards, ${usd(price, { whole: true })}`}
        />

        {/* Every stop is labelled and clickable, so the slider doubles as a
            price list — what a slider normally hides is exactly what a shopper
            wants to compare. Sized as real targets rather than tick marks: at
            label size these were a 14px tap area, which is a miss on a phone. */}
        <div className="mt-1 grid grid-cols-8 gap-1">
          {Array.from({ length: MAX_CARDS - MIN_CARDS + 1 }, (_, i) => MIN_CARDS + i).map((n) => {
            const on = n === config.cards
            return (
              <button
                key={n}
                onClick={() => onChange({ ...config, cards: n })}
                aria-label={`${n} cards, ${usd(priceFor(config.tier, n), { whole: true })}`}
                className="tnum py-1.5 text-center text-[0.6875rem] font-semibold transition-colors"
                style={{
                  color: on ? '#101010' : 'var(--color-faint)',
                  background: on ? accent : 'transparent',
                }}
              >
                {n}
              </button>
            )
          })}
        </div>
      </div>

      {/* Wrapping flex, not a three-column grid: "Guaranteed hits" and
          "Published RTP" are long enough to collide with each other in fixed
          columns once the panel is phone-width. */}
      <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-4 border-t pt-4">
        <div>
          <dt className="eyebrow">Price</dt>
          <dd className="tnum mt-1 text-2xl font-bold">{usd(price, { whole: true })}</dd>
        </div>
        <div>
          <dt className="eyebrow">Guaranteed hits</dt>
          <dd className="tnum mt-1 text-2xl font-bold">{hits}</dd>
        </div>
        <div>
          <dt className="eyebrow">Published RTP</dt>
          <dd className="tnum mt-1 text-2xl font-bold">{percent(rtp, 1)}</dd>
        </div>
      </dl>

      <button
        className="btn btn-primary btn-lg mt-5 w-full"
        onClick={onRip}
        disabled={pending || disabled}
      >
        {pending ? 'Opening…' : `Rip now · ${usd(price, { whole: true })}`}
      </button>
      {/* The purchase and the reveal are two different things, and only one of
          them is skippable. This button charges the instant it's pressed —
          the cards are rolled and land in the Vault right then, before the
          reveal animation even starts — so leaving mid-reveal costs nothing
          you haven't already been given. */}
      <p className="mt-2 text-center text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
        Charges immediately. Cards land in your Vault the instant you rip,
        so closing this before the reveal does not cost you anything.
      </p>

      {/*
        The one thing a card slider must not be allowed to imply. Adding cards
        raises the stake on each of them, which is why the price climbs faster
        than the count — but the share coming back is fixed by the tier, so a
        longer pack is more variance for more money, never a better deal.
      */}
      <p className="mt-3 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
        {config.cards === MIN_CARDS ? (
          <>
            {usd(base, { whole: true })} for the shortest pack. Each card you add costs more than
            the last and carries a bigger stake, but RTP stays at {percent(rtp, 1)} either way.
          </>
        ) : (
          <>
            {usd(price / config.cards)} of stake per card, against{' '}
            {usd(base / MIN_CARDS)} at {MIN_CARDS} cards. RTP is {percent(rtp, 1)} at every length,
            so this is more variance for more money, not a better price.
          </>
        )}
      </p>
    </div>
  )
}
